/*
 * A deliberately small, dependency-free mobile purchase affordance.
 *
 * The storefront keeps one real product form. This control only mirrors the
 * visible add-to-cart label, price and disabled state, then delegates a click
 * to that real form. It never duplicates variant state or submits its own
 * request, which keeps app-powered product forms and analytics intact.
 */
(() => {
  const mobileQuery = window.matchMedia('(max-width: 768px)');

  const initializeBar = (bar) => {
    if (bar.dataset.fsPurchaseBarInitialized === 'true') return;
    let form = document.getElementById(bar.dataset.fsFormId);
    if (!form) return;

    let sourceButton = form.querySelector('[data-add-to-cart]');
    const proxyButton = bar.querySelector('[data-fs-mobile-purchase-proxy]');
    const label = bar.querySelector('[data-fs-mobile-purchase-label]');
    const price = bar.querySelector('[data-fs-mobile-purchase-price]');
    const productScope = bar.closest('.product-single__meta') || document;

    if (!sourceButton || !proxyButton || !label || !price) return;

    bar.dataset.fsPurchaseBarInitialized = 'true';

    let proxySubmission = false;

    const privacyIsVisible = () => {
      const banner = document.querySelector('#shopify-pc__banner, .shopify-pc__banner');
      if (!banner) return false;
      const rect = banner.getBoundingClientRect();
      const style = window.getComputedStyle(banner);
      return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
    };

    const currentPriceText = () => {
      const priceElements = Array.from(productScope.querySelectorAll('.product-block--price [data-product-price]'));
      const priceElement = priceElements.find((element) => {
        if (element.closest('.hide, [hidden]')) return false;
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
      }) || priceElements[0];

      if (!priceElement) return '';

      // The price snippet outputs one visual value plus a visually-hidden copy
      // for screen readers. Reading the parent textContent concatenates both.
      const visualPrice = Array.from(priceElement.children).find((child) => (
        child.getAttribute('aria-hidden') === 'true' && !child.classList.contains('visually-hidden')
      ));

      if (visualPrice) return visualPrice.textContent.trim();

      const textOnlyClone = priceElement.cloneNode(true);
      textOnlyClone.querySelectorAll('.visually-hidden, [hidden], .hide').forEach((element) => element.remove());
      return textOnlyClone.textContent.trim();
    };

    const syncVisibility = () => {
      const rect = sourceButton?.getBoundingClientRect();
      const hasPassedButton = !!rect && rect.width > 0 && rect.height > 0 && rect.bottom <= 0 && window.scrollY > 0;
      const hasPrivacyOverlay = privacyIsVisible();
      document.documentElement.classList.toggle('fs-privacy-visible', hasPrivacyOverlay);
      const shouldShow = mobileQuery.matches && hasPassedButton && !hasPrivacyOverlay;
      bar.classList.toggle('is-visible', shouldShow);
      bar.setAttribute('aria-hidden', String(!shouldShow));
      bar.toggleAttribute('inert', !shouldShow);
      proxyButton.tabIndex = shouldShow && !proxyButton.disabled ? 0 : -1;
    };

    const sync = () => {
      const sourceLabel = sourceButton?.querySelector('[data-add-to-cart-text]');
      const isLoading = !!sourceButton && (sourceButton.classList.contains('btn--loading') || sourceButton.getAttribute('aria-busy') === 'true');
      const isDisabled = !sourceButton || sourceButton.disabled || sourceButton.getAttribute('aria-disabled') === 'true' || isLoading;

      label.textContent = (sourceLabel?.textContent || sourceButton?.textContent || label.textContent).trim();
      proxyButton.disabled = isDisabled;
      proxyButton.classList.toggle('btn--loading', isLoading);
      proxyButton.setAttribute('aria-busy', String(isLoading));
      price.textContent = currentPriceText() || price.textContent.trim();

      syncVisibility();
    };

    const observer = new IntersectionObserver((entries) => {
      const entry = entries.find((item) => item.target === sourceButton);
      if (!entry) return;
      syncVisibility();
    }, { threshold: 0 });

    observer.observe(sourceButton);
    mobileQuery.addEventListener('change', sync);

    let visibilityFrame = 0;
    const scheduleVisibility = () => {
      if (visibilityFrame) return;
      visibilityFrame = window.requestAnimationFrame(() => {
        visibilityFrame = 0;
        syncVisibility();
      });
    };
    window.addEventListener('scroll', scheduleVisibility, { passive: true });
    window.addEventListener('resize', scheduleVisibility);
    window.addEventListener('pageshow', scheduleVisibility);

    const bindPrivacyBanner = () => {
      const banner = document.querySelector('#shopify-pc__banner, .shopify-pc__banner');
      if (!banner || banner.dataset.fsPurchaseBarObserved === 'true') return false;
      banner.dataset.fsPurchaseBarObserved = 'true';
      new MutationObserver(sync).observe(banner, {
        attributes: true,
        attributeFilter: ['class', 'hidden', 'style'],
        childList: true,
        subtree: true
      });
      sync();
      return true;
    };

    if (!bindPrivacyBanner()) {
      const bodyObserver = new MutationObserver(() => {
        if (bindPrivacyBanner()) bodyObserver.disconnect();
      });
      if (document.body) {
        bodyObserver.observe(document.body, { childList: true });
      }
      window.setTimeout(() => bodyObserver.disconnect(), 8000);
    }

    const sourceObserver = new MutationObserver(sync);
    const observeSource = () => sourceObserver.observe(sourceButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'class'],
        childList: true,
        characterData: true,
        subtree: true
      });
    observeSource();

    const refreshSource = () => {
      const nextForm = document.getElementById(bar.dataset.fsFormId);
      const nextButton = nextForm?.querySelector('[data-add-to-cart]') || null;
      if (nextButton === sourceButton && nextForm === form) return;
      if (sourceButton) observer.unobserve(sourceButton);
      sourceObserver.disconnect();
      form = nextForm;
      sourceButton = nextButton;
      if (sourceButton) {
        observeSource();
        observer.observe(sourceButton);
      }
      sync();
    };

    new MutationObserver(refreshSource).observe(productScope, {
      childList: true,
      subtree: true
    });

    const priceBlock = productScope.querySelector('.product-block--price');
    if (priceBlock) {
      new MutationObserver(sync).observe(priceBlock, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    proxyButton.addEventListener('click', () => {
      refreshSource();
      sync();
      if (proxyButton.disabled) return;
      proxySubmission = true;
      sourceButton.click();
      sync();
    });

    productScope.addEventListener('ajaxProduct:added', (event) => {
      if (event.target === form) proxySubmission = false;
    });

    productScope.addEventListener('ajaxProduct:error', (event) => {
      if (event.target !== form || !proxySubmission) return;
      proxySubmission = false;
      const error = form.querySelector('.errors');
      if (!error) return;
      error.setAttribute('role', 'alert');
      error.setAttribute('tabindex', '-1');
      error.focus({ preventScroll: true });
      error.scrollIntoView({ block: 'center' });
    });

    sync();
  };

  const initialize = () => document
    .querySelectorAll('[data-fs-mobile-purchase-bar]')
    .forEach(initializeBar);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();

